import {
  Checkbox,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  type CheckboxProps,
} from "@mui/material";
import {
  useEffect,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";
import { ExpandLessIcon } from "../../assets/iconComponents/ExpandLessIcon";
import { PrintIcon } from "../../assets/iconComponents/PrintIcon";
import {
  CheckboxLabelWrapper,
  CollapsiblePlateletGrid,
  FetalAntigenFileItems,
  FetalAntigenIndentGrid,
  FetalAntigenIndentUploadGrid,
  FetalAntigenNestedOptions,
  FetalAntigenPrintIconSlot,
  FetalAntigenPrintoutMessage,
  NewReqFetalAntigenGrid,
  NewReqConfirmationMessageText,
  OptionLabel,
  PlateletAntigensHeader,
  PlateletAntigensToggle,
  RadioFormControl,
} from "./NewRequisition.styles";
import NewReqFileUploader, { FileUploadInfo } from "./NewReqFileUploader";
import { NewReqFileUploadAntigenTextWrapper } from "./NewReqFileUploader.styles";
import {
  PLATELET_ANTIGENS,
  PLATELET_GROUP_NAME,
  RBC_ANTIGENS,
  RBC_GROUP_NAME,
  type AntigenConfig,
  type FetalAntigenFieldSelection,
  type PlateletAntigenConfig,
} from "./fetalAntigenConstants";
import { unselectedCheckboxSx } from "./testPanelStyles";

export type FetalAntigenFieldsValue = {
  isRbc: boolean;
  isPlatelet: boolean;
  selected: FetalAntigenFieldSelection;
};

type FetalAntigenOptionsProps = {
  antigens: (AntigenConfig | PlateletAntigenConfig)[];
  disabled: boolean;
  rhdIncluded: boolean;
  selected: FetalAntigenFieldSelection;
  onChange: NonNullable<CheckboxProps["onChange"]>;
};

type FetalAntigenFieldsCommonProps = {
  value: FetalAntigenFieldsValue;
  disabled: boolean;
  rhdIncluded: boolean;
  expandedAntigensEnabled: boolean;
  fileUrls: FileUploadInfo;
  uploadProcessing: boolean;
  preflight: boolean;
  documentPrompt: ReactNode;
  rbcError?: boolean;
  plateletError?: boolean;
  nestedTopMargin?: boolean;
  onChange: (value: FetalAntigenFieldsValue) => void;
  onFileUrlsChange: (fileUrls: FileUploadInfo) => void;
  onUploadProcessingChange: Dispatch<SetStateAction<boolean>>;
};

type PrintedDocumentFieldsProps = {
  allowPrintedDocument: true;
  requireFile: boolean;
  printedOptionLabel: ReactNode;
  printedMessage: ReactNode;
  onRequireFileChange: (requireFile: boolean) => void;
};

type RequiredUploadFieldsProps = {
  allowPrintedDocument: false;
};

type FetalAntigenFieldsProps = FetalAntigenFieldsCommonProps &
  (PrintedDocumentFieldsProps | RequiredUploadFieldsProps);

const FetalAntigenOptions = ({
  antigens,
  disabled,
  rhdIncluded,
  selected,
  onChange,
}: FetalAntigenOptionsProps) => (
  <>
    {antigens.map((antigen) => (
      <CheckboxLabelWrapper
        key={antigen.name}
        label={
          antigen.sublabel ? (
            <OptionLabel>
              {`${antigen.label} `}
              <small>{antigen.sublabel}</small>
            </OptionLabel>
          ) : (
            <OptionLabel>{antigen.label}</OptionLabel>
          )
        }
        control={
          <Checkbox
            sx={unselectedCheckboxSx}
            checked={Boolean(selected[antigen.name])}
            disabled={
              disabled || Boolean(antigen.disabledWhenRhd && rhdIncluded)
            }
            name={antigen.name}
            onChange={onChange}
          />
        }
      />
    ))}
  </>
);

export const FetalAntigenFields = (props: FetalAntigenFieldsProps) => {
  const {
    value,
    disabled,
    rhdIncluded,
    expandedAntigensEnabled,
    fileUrls,
    uploadProcessing,
    preflight,
    documentPrompt,
    rbcError = false,
    plateletError = false,
    nestedTopMargin = false,
    onChange,
    onFileUrlsChange,
    onUploadProcessingChange,
  } = props;
  const requireFile = props.allowPrintedDocument ? props.requireFile : true;
  // Expand platelet extras when a non-primary antigen is already selected.
  const hasOtherPlateletAntigens = PLATELET_ANTIGENS.filter(
    (antigen) => !antigen.isPrimary,
  ).some((antigen) => Boolean(value.selected[antigen.name]));
  const [showAllPlatelets, setShowAllPlatelets] = useState(
    hasOtherPlateletAntigens,
  );

  useEffect(() => {
    if (hasOtherPlateletAntigens) {
      setShowAllPlatelets(true);
    }
  }, [hasOtherPlateletAntigens]);

  const updateGroup = (
    group: "isRbc" | "isPlatelet",
    checked: boolean,
    antigens: (AntigenConfig | PlateletAntigenConfig)[],
  ) => {
    const selected = { ...value.selected };
    if (!checked) {
      antigens.forEach((antigen) => {
        selected[antigen.name] = false;
      });
    }
    onChange({ ...value, [group]: checked, selected });
  };

  const updateAntigen: NonNullable<CheckboxProps["onChange"]> = (event) => {
    const { name, checked } = event.target;
    onChange({
      ...value,
      selected: { ...value.selected, [name]: checked },
    });
  };

  return (
    <>
      <CheckboxLabelWrapper
        disabled={disabled}
        label={
          <OptionLabel $tone={rbcError ? "danger" : "default"}>
            Add <strong>{RBC_GROUP_NAME}</strong>
          </OptionLabel>
        }
        control={
          <Checkbox
            sx={unselectedCheckboxSx}
            checked={value.isRbc}
            disabled={disabled}
            name="isFetalAntigenRbc"
            onChange={(event) =>
              updateGroup("isRbc", event.target.checked, RBC_ANTIGENS)
            }
            inputProps={{ "aria-label": `Add ${RBC_GROUP_NAME}` }}
          />
        }
      />

      {value.isRbc && (
        <FetalAntigenNestedOptions $topMargin={nestedTopMargin}>
          <NewReqFetalAntigenGrid>
            <FetalAntigenOptions
              antigens={RBC_ANTIGENS.filter(
                (antigen) => antigen.isOriginal || expandedAntigensEnabled,
              )}
              disabled={disabled}
              rhdIncluded={rhdIncluded}
              selected={value.selected}
              onChange={updateAntigen}
            />
          </NewReqFetalAntigenGrid>
        </FetalAntigenNestedOptions>
      )}

      {expandedAntigensEnabled && (
        <CheckboxLabelWrapper
          disabled={disabled}
          label={
            <OptionLabel $tone={plateletError ? "danger" : "default"}>
              Add <strong>{PLATELET_GROUP_NAME}</strong>
            </OptionLabel>
          }
          control={
            <Checkbox
              sx={unselectedCheckboxSx}
              checked={value.isPlatelet}
              disabled={disabled}
              name="isFetalAntigenPlatelet"
              onChange={(event) =>
                updateGroup(
                  "isPlatelet",
                  event.target.checked,
                  PLATELET_ANTIGENS,
                )
              }
              inputProps={{ "aria-label": `Add ${PLATELET_GROUP_NAME}` }}
            />
          }
        />
      )}

      {expandedAntigensEnabled && value.isPlatelet && (
        <FetalAntigenNestedOptions $topMargin={nestedTopMargin}>
          <PlateletAntigensHeader>
            <FetalAntigenOptions
              antigens={PLATELET_ANTIGENS.filter(
                (antigen) => antigen.isPrimary,
              )}
              disabled={disabled}
              rhdIncluded={rhdIncluded}
              selected={value.selected}
              onChange={updateAntigen}
            />
            <PlateletAntigensToggle
              type="button"
              onClick={() => setShowAllPlatelets((current) => !current)}
              data-expanded={showAllPlatelets}
            >
              <OptionLabel>
                <strong>
                  {showAllPlatelets
                    ? "Show fewer antigens"
                    : "Show more antigens"}
                </strong>
              </OptionLabel>
              <ExpandLessIcon />
            </PlateletAntigensToggle>
          </PlateletAntigensHeader>

          <CollapsiblePlateletGrid isExpanded={showAllPlatelets}>
            <FetalAntigenOptions
              antigens={PLATELET_ANTIGENS.filter(
                (antigen) => !antigen.isPrimary,
              )}
              disabled={disabled}
              rhdIncluded={rhdIncluded}
              selected={value.selected}
              onChange={updateAntigen}
            />
          </CollapsiblePlateletGrid>
        </FetalAntigenNestedOptions>
      )}

      {(value.isRbc || value.isPlatelet) && (
        <>
          <NewReqFileUploadAntigenTextWrapper>
            {documentPrompt}
          </NewReqFileUploadAntigenTextWrapper>
          {props.allowPrintedDocument ? (
            <RadioFormControl $hideBorder>
              <RadioGroup
                value={String(requireFile)}
                onChange={(event) =>
                  props.onRequireFileChange(event.target.value === "true")
                }
                row
              >
                <FetalAntigenFileItems>
                  <Grid item>
                    <FormControlLabel
                      value="true"
                      control={<Radio />}
                      label={
                        <OptionLabel $tone="muted">
                          Upload a lab report or consultation note
                        </OptionLabel>
                      }
                    />
                  </Grid>
                  {requireFile && (
                    <FetalAntigenIndentUploadGrid>
                      <NewReqFileUploader
                        type="antigen"
                        value={fileUrls}
                        setValue={onFileUrlsChange}
                        uploadProcessing={uploadProcessing}
                        setUploadProcessing={onUploadProcessingChange}
                        preflight={preflight}
                      />
                    </FetalAntigenIndentUploadGrid>
                  )}
                  <Grid item>
                    <FormControlLabel
                      value="false"
                      control={<Radio />}
                      label={props.printedOptionLabel}
                    />
                  </Grid>
                  {!requireFile && (
                    <FetalAntigenIndentGrid item xs={12}>
                      <FetalAntigenPrintoutMessage>
                        <FetalAntigenPrintIconSlot>
                          <PrintIcon />
                        </FetalAntigenPrintIconSlot>
                        <NewReqConfirmationMessageText>
                          {props.printedMessage}
                        </NewReqConfirmationMessageText>
                      </FetalAntigenPrintoutMessage>
                    </FetalAntigenIndentGrid>
                  )}
                </FetalAntigenFileItems>
              </RadioGroup>
            </RadioFormControl>
          ) : (
            <NewReqFileUploader
              type="antigen"
              value={fileUrls}
              setValue={onFileUrlsChange}
              uploadProcessing={uploadProcessing}
              setUploadProcessing={onUploadProcessingChange}
              preflight={preflight}
            />
          )}
        </>
      )}
    </>
  );
};
